import React from "react";
import { Link } from "react-router-dom";
import { Clock, Star } from "lucide-react";
import { useI18n } from "@/i18n";

const Chip = ({ children, tone = "dark", icon, testid }) => {
  const tones = {
    dark: "bg-black/50 text-white",
    red: "bg-[#8A281E] text-white",
    orange: "bg-[#BC5D2E] text-white",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full backdrop-blur-md px-3 py-1 text-[11px] font-medium ${tones[tone]}`}>
      {icon}{children}
    </span>
  );
};

export default function ExpeditionCard({ exp, index = 0 }) {
  const { L, t } = useI18n();
  const comingSoon = exp.status === "coming_soon";
  const soldOut = !comingSoon && (exp.sold_out || exp.availability === "waitlist");
  const price = Number(exp.price) || 0;
  const promo = exp.promo_price ? Number(exp.promo_price) : null;
  const off = promo && price ? Math.round((1 - promo / price) * 100) : 0;

  return (
    <Link
      to={`/expeditions/${exp.slug}`}
      data-testid={`expedition-card-${exp.slug}`}
      className="group block fade-up"
      style={{ animationDelay: `${index * 0.08}s` }}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#121212]">
        <img
          src={exp.cover}
          alt={L(exp.title)}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />

        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {comingSoon && <Chip tone="orange" testid={`comingsoon-${exp.slug}`}>{t("coming_soon_badge")}</Chip>}
          {!comingSoon && exp.best_seller && (
            <Chip tone="orange" testid={`bestseller-${exp.slug}`} icon={<Star size={11} className="fill-current" />}>Best Seller</Chip>
          )}
        </div>

        {soldOut && (
          <div className="absolute top-3 right-3">
            <Chip tone="red" testid={`availability-${exp.slug}`}>{t("avail_full")}</Chip>
          </div>
        )}

        <div className="absolute bottom-3 left-3">
          <Chip testid={`duration-${exp.slug}`} icon={<Clock size={12} />}>{exp.duration_days} {t("days")}</Chip>
        </div>
      </div>

      <div className="pt-4">
        <span className="font-mono-hs text-[10px] uppercase tracking-[0.25em] text-[#BC5D2E]">{L(exp.location)}</span>
        <h3 className="font-clash text-xl sm:text-2xl uppercase tracking-tight leading-tight text-[#F5F5F0] mt-1.5 transition-colors group-hover:text-white">{L(exp.title)}</h3>
        <div className="mt-3 flex items-end flex-wrap gap-x-2.5 gap-y-1">
          <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("from")}</span>
          {promo ? (
            <>
              <span className="font-clash text-xl text-[#F5F5F0] leading-none">{promo.toLocaleString("fr-FR")} €</span>
              <span className="font-mono-hs text-xs text-[#737373] line-through">{price.toLocaleString("fr-FR")} €</span>
              {off > 0 && (
                <span data-testid={`promo-${exp.slug}`} className="font-mono-hs text-[10px] font-semibold uppercase tracking-widest text-[#BC5D2E] bg-[#BC5D2E]/12 px-1.5 py-0.5">-{off}%</span>
              )}
            </>
          ) : (
            <span className="font-clash text-xl text-[#F5F5F0] leading-none">{price.toLocaleString("fr-FR")} €</span>
          )}
        </div>
      </div>
    </Link>
  );
}
